(() => {
  if (customElements.get('xo-price-amount')) return;

  // Shopify Liquid 金额使用百分之一货币单位，包括 JPY；不能用显示小数位推算。
  const currencyAmount = (element) => Number(element.dataset.xoPriceValue) / 100;

  function measure(text, element) {
    const style = getComputedStyle(element);
    const probe = document.createElement('span');
    Object.assign(probe.style, {
      position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap',
      font: style.font, letterSpacing: style.letterSpacing,
    });
    probe.textContent = text;
    document.body.appendChild(probe);
    const width = probe.getBoundingClientRect().width;
    probe.remove();
    return width;
  }

  function compact(element, decimals) {
    const amount = currencyAmount(element);
    if (!Number.isFinite(amount) || Math.abs(amount) < 1000) return '';
    const magnitude = Math.abs(amount);
    const [divisor, suffix] = magnitude >= 1e9 ? [1e9, 'B'] : magnitude >= 1e6 ? [1e6, 'M'] : [1e3, 'K'];
    try {
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: element.dataset.xoPriceCurrency,
        currencyDisplay: element.dataset.xoPriceCurrencyDisplay || 'symbol',
        minimumFractionDigits: 0, maximumFractionDigits: decimals,
      });
      return `≈ ${formatter.format(amount / divisor)}${suffix}`;
    } catch {
      return '';
    }
  }

  function fitCompact(element, width) {
    let text = compact(element, 2);
    for (let decimals = 1; text && measure(text, element) > width && decimals >= 0; decimals--) {
      text = compact(element, decimals);
    }
    return text;
  }

  function updateGroup(group) {
    if (!group.isConnected || !group.clientWidth) return;
    const amounts = [...group.querySelectorAll('xo-price-amount')];
    const available = (element) => {
      const saving = element.closest('.xo-price__saving');
      if (!saving) return group.clientWidth;
      const label = saving.querySelector('.xo-price__saving-label');
      return group.clientWidth - (label?.getBoundingClientRect().width || 0) - (parseFloat(getComputedStyle(saving).columnGap) || 0);
    };
    // 同组金额统一采用完整或近似展示，避免现价和原价数量级难以比较。
    const needsCompact = amounts.some(element => compact(element, 2) && measure(element.full.textContent, element) > available(element));
    for (const element of amounts) {
      const saving = element.closest('.xo-price__saving');
      const width = available(element);
      let text = needsCompact ? fitCompact(element, width) : '';
      // 极窄空间中允许 Save 和金额分行，保留币种与数量级。
      const stacked = Boolean(saving && measure(text || element.full.textContent, element) > width);
      saving?.classList.toggle('xo-price__saving--stacked', stacked);
      if (stacked && text) {
        text = fitCompact(element, group.clientWidth);
      }
      element.compact.textContent = text;
      element.toggleAttribute('xo-compact', Boolean(text));
      if (text) {
        element.setAttribute('role', 'button');
        element.tabIndex = 0;
        element.setAttribute('aria-expanded', String(element.hasAttribute('xo-expanded')));
      } else {
        element.removeAttribute('xo-expanded');
        element.removeAttribute('aria-expanded');
        element.removeAttribute('role');
        element.removeAttribute('tabindex');
      }
    }
  }

  // 现价、原价和 Save 共用一次测量及监听，避免每个金额重复计算整组。
  const groupWatchers = new WeakMap();

  function watchGroup(group) {
    let watcher = groupWatchers.get(group);
    if (!watcher) {
      watcher = { count: 0, frame: 0 };
      watcher.schedule = () => {
        if (!group.isConnected || groupWatchers.get(group) !== watcher) return;
        cancelAnimationFrame(watcher.frame);
        watcher.frame = requestAnimationFrame(() => updateGroup(group));
      };
      watcher.release = () => {
        if (--watcher.count) return;
        watcher.observer.disconnect();
        document.fonts.removeEventListener('loadingdone', watcher.schedule);
        cancelAnimationFrame(watcher.frame);
        groupWatchers.delete(group);
      };
      groupWatchers.set(group, watcher);
      watcher.observer = new ResizeObserver(watcher.schedule);
      watcher.observer.observe(group);
      document.fonts.addEventListener('loadingdone', watcher.schedule);
      document.fonts.ready.then(watcher.schedule);
    }
    watcher.count++;
    watcher.schedule();
    return watcher;
  }

  class XoPriceAmount extends HTMLElement {
    connectedCallback() {
      this.full = this.querySelector('.xo-price-amount__full');
      this.compact = this.querySelector('.xo-price-amount__compact');
      this.group = this.closest('.xo-price__container');
      if (!this.full || !this.compact || !this.group) return;
      this.onClick = () => {
        if (!this.hasAttribute('xo-compact')) return;
        this.toggleAttribute('xo-expanded');
        this.setAttribute('aria-expanded', String(this.hasAttribute('xo-expanded')));
      };
      this.onKeydown = event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          this.onClick();
        }
      };
      this.onKeyup = event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
        }
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('keyup', this.onKeyup);
      this.watcher = watchGroup(this.group);
    }

    disconnectedCallback() {
      this.watcher?.release();
      this.watcher = null;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('keyup', this.onKeyup);
    }
  }

  customElements.define('xo-price-amount', XoPriceAmount);
})();
